import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { authService, UserSettings } from '../../services/authService';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const THEMES = [
    { id: 'traditional', name: 'Truyền thống', color: '#DC2626' },
    { id: 'minimal', name: 'Tối giản', color: '#6B7280' },
    { id: 'nature', name: 'Thiên nhiên', color: '#059669' },
    { id: 'lotus', name: 'Hoa Sen', color: '#DB2777' },
];

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
    const { user } = useAuthStore();
    const [settings, setSettings] = useState<UserSettings>({
        defaultTheme: 'traditional',
        notifications: { enabled: true },
        preferences: { language: 'vi', timezone: 'Asia/Ho_Chi_Minh' },
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // Load current settings
    useEffect(() => {
        if (isOpen && user) {
            loadSettings();
        }
    }, [isOpen, user]);

    const loadSettings = async () => {
        try {
            const userSettings = await authService.getSettings();
            if (userSettings) {
                setSettings({
                    defaultTheme: userSettings.defaultTheme || 'traditional',
                    notifications: userSettings.notifications || { enabled: true },
                    preferences: userSettings.preferences || { language: 'vi', timezone: 'Asia/Ho_Chi_Minh' },
                });
            }
        } catch (error) {
            console.error('Failed to load settings:', error);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage('');
        try {
            await authService.updateSettings(settings);
            setSaveMessage('Đã lưu cài đặt!');
            setTimeout(() => {
                setSaveMessage('');
                onClose();
            }, 1000);
        } catch (error) {
            console.error('Failed to save settings:', error);
            setSaveMessage('Lỗi khi lưu cài đặt');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-gray-700 to-gray-600 px-6 py-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Cài đặt
                    </h2>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Theme Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Giao diện mặc định
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            {THEMES.map((theme) => (
                                <button
                                    key={theme.id}
                                    onClick={() => setSettings({ ...settings, defaultTheme: theme.id })}
                                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2
                                        ${settings.defaultTheme === theme.id
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div
                                        className="w-4 h-4 rounded-full"
                                        style={{ backgroundColor: theme.color }}
                                    />
                                    <span className="text-sm font-medium text-gray-700">{theme.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Language */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Ngôn ngữ
                        </label>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setSettings({
                                    ...settings,
                                    preferences: { ...settings.preferences, language: 'vi' }
                                })}
                                className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2
                                    ${settings.preferences?.language === 'vi'
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <span className="text-red-600 font-bold text-sm">VN</span>
                                <span className="text-sm font-medium text-gray-700">Tiếng Việt</span>
                            </button>
                            <button
                                onClick={() => setSettings({
                                    ...settings,
                                    preferences: { ...settings.preferences, language: 'en' }
                                })}
                                className={`flex-1 p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2
                                    ${settings.preferences?.language === 'en'
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <span className="text-blue-600 font-bold text-sm">EN</span>
                                <span className="text-sm font-medium text-gray-700">English</span>
                            </button>
                        </div>
                    </div>

                    {/* Notifications */}
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Thông báo
                            </label>
                            <p className="text-xs text-gray-500">Nhận thông báo về sự kiện</p>
                        </div>
                        <button
                            onClick={() => setSettings({
                                ...settings,
                                notifications: {
                                    ...settings.notifications,
                                    enabled: !settings.notifications?.enabled
                                }
                            })}
                            className={`relative w-12 h-6 rounded-full transition-colors
                                ${settings.notifications?.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                        >
                            <div
                                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform
                                    ${settings.notifications?.enabled ? 'left-7' : 'left-1'}`}
                            />
                        </button>
                    </div>

                    {/* Save Message */}
                    {saveMessage && (
                        <div className={`p-3 rounded-xl text-center text-sm font-medium
                            ${saveMessage.includes('Lỗi') ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                            {saveMessage}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 pb-6 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-xl
                            hover:bg-gray-200 transition-all"
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex-1 py-3 px-4 bg-blue-600 text-white font-semibold rounded-xl
                            hover:bg-blue-700 disabled:opacity-50 transition-all"
                    >
                        {isSaving ? 'Đang lưu...' : 'Lưu cài đặt'}
                    </button>
                </div>

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    aria-label="Đóng"
                >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default SettingsModal;
